import {MenuItem, SxProps, Theme} from '@mui/material'

import {useFormInputLabelWithSuffix} from '../../hooks'
import {FormFieldBase, SelectOption} from '../../types'
import {BaseTextField} from '../formFields'

export type SelectProps<TOptionValue extends string | number> = FormFieldBase & {
  options: SelectOption<TOptionValue>[]
  value?: TOptionValue
  onChange: (value?: TOptionValue) => void
  getOptionLabel?: (option: SelectOption<TOptionValue>) => React.ReactNode
  sx?: SxProps<Theme>
  'data-test-id'?: string
  onOptionClick?: (option: SelectOption<TOptionValue>) => void
}

export const Select = <TOptionValue extends string | number>({
  label,
  name,
  options,
  value,
  onChange,
  getOptionLabel = ({label}) => label,
  disabled,
  required,
  readOnly,
  optional,
  error,
  helperText,
  sx,
  'data-test-id': dataTestId,
  onOptionClick
}: SelectProps<TOptionValue>) => {
  const inputLabel = useFormInputLabelWithSuffix({label, optional})

  return (
    <BaseTextField
      data-test-id={dataTestId}
      fullWidth
      label={inputLabel}
      required={required}
      onChange={(event) => {
        const val = event.target.value as unknown as TOptionValue | undefined
        if (!val) onChange(undefined)
        else onChange(val)
      }}
      name={name}
      value={value ?? []}
      select
      disabled={readOnly ? false : disabled}
      InputProps={{
        readOnly
      }}
      error={error}
      helperText={helperText}
      sx={sx}
    >
      {options.map((option) => (
        <MenuItem
          data-test-id={`${dataTestId}-${option.value}`}
          key={option.value}
          value={option.value}
          selected={option.value === value}
          onClick={() => onOptionClick?.(option)}
        >
          {getOptionLabel(option)}
        </MenuItem>
      ))}
    </BaseTextField>
  )
}
